/**
 * The copy of a support report CorpBrain sends to the organization's monday
 * board — the pure part (no DOM globals, no network), so a node test can hold
 * it to its two promises:
 *
 *   1. It never stands in PanLuma's way. The Send handler reads the panel
 *      synchronously, never calls preventDefault / stopPropagation, and
 *      schedules everything else (the board check, the screenshot, the upload)
 *      for after the click has gone on to PanLuma's own handler.
 *   2. It sends what the person sent: the subject, the description (its basic
 *      formatting), the priority, the page and the app's pages before it
 *      (paths only); the screenshot only when they left "Include screenshot"
 *      on — PanLuma's own, WITH the marks they drew in its Annotate editor
 *      (the panel's preview image), ours only when that cannot be read; the
 *      console lines (every level, the last 50 since page load) only when they
 *      left "Include console output" on, and the images they picked. When they pressed Support inside a
 *      conversation, its id — never an agent's name: the server names the
 *      agent, and only from a conversation they may read.
 *
 * The board is never chosen here — the server resolves it from the sender's
 * own organization (backend/modules/support_board).
 */

/** The widget's element ids (the same panel supportWidget.ts arranges). */
export const PANEL_IDS = {
  submit: 'panluma-widget-submit',
  form: 'panluma-widget-form',
  subject: 'panluma-title',
  description: 'panluma-description',
  priority: 'panluma-priority',
  screenshot: 'panluma-include-screenshot',
  console: 'panluma-include-console',
  files: 'panluma-attach-input',
  /** PanLuma's screenshot preview; after Annotate → Done its src is the marked image. */
  screenshotImg: 'panluma-screenshot-img',
} as const;

export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export interface CapturedReport {
  subject: string;
  descriptionHtml: string;
  descriptionText: string;
  priority: string;
  pageUrl: string;
  /** The conversation on screen at Send, if any (its id only). */
  conversationId: string | null;
  /** The app's pages before this one, oldest first (paths only). */
  history: NavigationEntry[];
  includeScreenshot: boolean;
  includeConsole: boolean;
  /** PanLuma's screenshot as the person left it (annotations included), a
   *  data: URL read at the click; null when absent or not an image. */
  screenshotDataUrl: string | null;
  files: File[];
}

interface PanelElement {
  value?: string;
  checked?: boolean;
  src?: string;
  innerHTML?: string;
  textContent?: string | null;
  files?: ArrayLike<File> | null;
}

export interface PanelLookup {
  getElementById(id: string): PanelElement | null;
}

/** What the person submitted, read from the panel as it is at Send. */
export function readPanel(doc: PanelLookup, pageUrl: string, context: SendContext = {}): CapturedReport | null {
  const subject = doc.getElementById(PANEL_IDS.subject);
  const description = doc.getElementById(PANEL_IDS.description);
  if (!subject || !description) return null;
  const files = Array.from(doc.getElementById(PANEL_IDS.files)?.files ?? [])
    .filter(f => f.size <= MAX_FILE_BYTES)
    .slice(0, MAX_FILES);
  return {
    subject: (subject.value ?? '').trim(),
    descriptionHtml: description.innerHTML ?? '',
    descriptionText: (description.textContent ?? '').trim(),
    priority: doc.getElementById(PANEL_IDS.priority)?.value ?? '',
    pageUrl,
    conversationId: context.conversationId ?? null,
    history: context.history ?? [],
    // The widget's own opt-outs: unchecked = not sent, to PanLuma or to us.
    includeScreenshot: doc.getElementById(PANEL_IDS.screenshot)?.checked ?? false,
    includeConsole: doc.getElementById(PANEL_IDS.console)?.checked ?? false,
    screenshotDataUrl: imageDataUrl(doc.getElementById(PANEL_IDS.screenshotImg)?.src),
    files,
  };
}

/** A report the widget itself would refuse is not copied either. */
export function isComplete(report: CapturedReport | null): report is CapturedReport {
  return !!report && !!report.subject && !!report.descriptionText;
}

// ── Redaction (the same kinds the widget removes before sending; the server's
// patterns are the same — tests/fixtures/support_redaction_cases.json holds both to it) ──

const REDACTIONS: Array<[RegExp, string]> = [
  [/eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g, '[token]'],
  [/\bbearer\s+[A-Za-z0-9._~+/=-]{8,}/gi, 'Bearer [token]'],
  [/\b(?:(?:cbp|cbr|plma|sk|pk|ghp|xox[abp])_|sk-)[A-Za-z0-9_-]{8,}/g, '[key]'],
  // access_token=…, "token":"…", user_password: … (a name ending in one of these, quoted or not)
  [/((?:password|passwd|secret|token|api[_-]?key|authorization)["']?\s*[:=]\s*["']?)[^\s"',;&]+/gi, '$1[redacted]'],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]'],
  [/\b\d(?:[ -]?\d){12,18}\b/g, '[number]'],
];

export const CONSOLE_LINES = 50;
export const CONSOLE_LINE_MAX = 500;
/** Every level, as PanLuma's own console section records them. */
export const CONSOLE_LEVELS = ['log', 'info', 'warn', 'error', 'debug'] as const;

// ── The screenshot PanLuma holds ─────────────────────────────────────────

const IMAGE_DATA_URL = /^data:(image\/(?:png|jpeg|webp|gif));base64,([A-Za-z0-9+/=]+)$/;

/** A data: URL of an image (what PanLuma's preview holds), else null. */
export function imageDataUrl(src: string | undefined | null): string | null {
  return src && IMAGE_DATA_URL.test(src) ? src : null;
}

/** The image a data: URL holds, decoded here (no fetch), and its file name. */
export function dataUrlImage(dataUrl: string): { blob: Blob; name: string } | null {
  const match = IMAGE_DATA_URL.exec(dataUrl);
  if (!match) return null;
  try {
    const binary = atob(match[2]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const ext = match[1].split('/')[1].replace('jpeg', 'jpg');
    return { blob: new Blob([bytes], { type: match[1] }), name: `screenshot.${ext}` };
  } catch {
    return null;
  }
}

export function redactLine(line: string): string {
  let out = line.slice(0, CONSOLE_LINE_MAX * 2);
  for (const [pattern, replacement] of REDACTIONS) out = out.replace(pattern, replacement);
  return out.slice(0, CONSOLE_LINE_MAX);
}

// ── The pages before this one ────────────────────────────────────────────

export interface NavigationEntry { path: string; at: string }
export interface SendContext { conversationId?: string | null; history?: NavigationEntry[] }

export const HISTORY_ENTRIES = 10;

/** The app's last pages, paths only — a query or a fragment may carry a token. */
export class NavigationRing {
  private entries: NavigationEntry[] = [];

  push(href: string, at: Date): void {
    let path: string;
    try {
      path = new URL(href, 'http://local').pathname;
    } catch {
      return;
    }
    const last = this.entries[this.entries.length - 1];
    if (last && last.path === path) return;
    this.entries.push({ path: path.slice(0, 200), at: at.toISOString() });
    if (this.entries.length > HISTORY_ENTRIES) this.entries.splice(0, this.entries.length - HISTORY_ENTRIES);
  }

  recent(): NavigationEntry[] {
    return [...this.entries];
  }
}

/** A bounded ring of recent, redacted console lines. */
export class ConsoleRing {
  private lines: string[] = [];

  push(level: string, parts: unknown[]): void {
    const text = parts.map(part => {
      if (part instanceof Error) return `${part.name}: ${part.message}`;
      if (typeof part === 'string') return part;
      try { return JSON.stringify(part); } catch { return String(part); }
    }).join(' ');
    this.lines.push(redactLine(`[${level}] ${text}`));
    if (this.lines.length > CONSOLE_LINES) this.lines.splice(0, this.lines.length - CONSOLE_LINES);
  }

  recent(): string[] {
    return [...this.lines];
  }
}

// ── The form the server receives ─────────────────────────────────────────

export interface FormLike {
  append(name: string, value: string | Blob, filename?: string): void;
}

export function fillReportForm(
  form: FormLike,
  report: CapturedReport,
  extras: { screenshot: Blob | null; screenshotName?: string; browser: Record<string, string>; console: string[] },
): FormLike {
  form.append('subject', report.subject);
  form.append('description_html', report.descriptionHtml);
  form.append('priority', report.priority);
  form.append('page_url', report.pageUrl);
  if (report.conversationId) form.append('conversation_id', report.conversationId);
  form.append('technical', JSON.stringify({
    browser: extras.browser,
    history: report.history,
    // Whether they left "Include console output" on: the board then says "No
    // console output" or "Not included", never an empty field.
    console_included: report.includeConsole,
    ...(report.includeConsole ? { console: extras.console } : {}),
  }));
  if (report.includeScreenshot && extras.screenshot) {
    form.append('screenshot', extras.screenshot, extras.screenshotName ?? 'screenshot.jpg');
  }
  for (const file of report.files) form.append('files', file, file.name);
  return form;
}

// ── Send: read now, send later ───────────────────────────────────────────

export interface SendDeps {
  doc: PanelLookup;
  pageUrl: () => string;
  /** The conversation on screen and the pages before, read at the click. */
  context?: () => SendContext;
  /** Run after the click has gone on to PanLuma (setTimeout 0 in the browser). */
  schedule: (task: () => void) => void;
  /** The upload itself (checks the board, takes the screenshot, posts). */
  send: (report: CapturedReport) => Promise<void>;
  now: () => number;
  warn: (message: string) => void;
}

const DUPLICATE_WINDOW_MS = 3000;
let lastSent: { subject: string; at: number } | null = null;

/** Reset between tests. */
export function resetSendGuard(): void {
  lastSent = null;
}

/**
 * The Send listener. Reads the panel and returns at once; never touches the
 * event. A click and the form's submit for the same report count once.
 */
export function onPanelSend(_event: unknown, deps: SendDeps): void {
  let report: CapturedReport | null;
  try {
    report = readPanel(deps.doc, deps.pageUrl(), deps.context?.() ?? {});
  } catch {
    deps.warn('[support] the support panel could not be read; PanLuma still gets the report');
    return;
  }
  if (!isComplete(report)) return;
  const now = deps.now();
  if (lastSent && lastSent.subject === report.subject && now - lastSent.at < DUPLICATE_WINDOW_MS) return;
  lastSent = { subject: report.subject, at: now };
  const captured = report;
  deps.schedule(() => {
    deps.send(captured).catch(() => deps.warn('[support] the copy for the monday board was not sent'));
  });
}
